import type { ApiErrorBody } from '@/api/types';

/** An error carrying the backend's machine-readable code, so callers branch on
 *  `code` and display `message` rather than matching on English text. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
    this.requestId = body.request_id;
  }

  /** True when re-authentication would let the same request through. */
  get needsStepUp(): boolean {
    return this.code === 'STEP_UP_REQUIRED';
  }

  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Could not reach the server.') {
    super(message);
    this.name = 'NetworkError';
  }
}

/** Message suitable for display. Errors explain what happened and what to do
 *  next; they do not apologise and are never vague. */
export function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof NetworkError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
